import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '../common';

interface FrontMatterEditorLayoutProps {
  title: string;
  bookId: string;
  isLoading: boolean;
  isSaving: boolean;
  isVisible: boolean;
  onVisibilityChange: (visible: boolean) => void;
  onSave: () => Promise<void>;
  error?: string | null;
  children: ReactNode;
  preview: ReactNode;
}

export function FrontMatterEditorLayout({
  title,
  bookId,
  isLoading,
  isSaving,
  isVisible,
  onVisibilityChange,
  onSave,
  error,
  children,
  preview,
}: FrontMatterEditorLayoutProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleCancel = () => {
    navigate(`/books/${bookId}`);
  };

  const handleSave = async () => {
    await onSave();
    navigate(`/books/${bookId}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Back button and title */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleCancel}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h1>
            </div>

            {/* Right: Visibility toggle and actions */}
            <div className="flex items-center gap-3">
              {/* Visibility toggle */}
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {t('frontMatter.visible')}
                </span>
                <button
                  type="button"
                  onClick={() => onVisibilityChange(!isVisible)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    isVisible ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      isVisible ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </label>

              <div className="h-6 w-px bg-gray-200 dark:bg-gray-700" />

              <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
            {error}
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Editor controls */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">
              {t('frontMatter.settings')}
            </h2>
            {children}
          </div>

          {/* Preview */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">
              {t('frontMatter.preview')}
            </h2>
            <div className="bg-amber-50 dark:bg-gray-900 rounded-lg p-8 min-h-[400px] flex items-center justify-center">
              {preview}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FrontMatterEditorLayout;
